import { ReactNode, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
import { ScreensProps, UserProgressScreensProps } from "../../../contexts/OnboardingContext";
import { useOnboarding } from "../../../hooks/useOnboarding";
import { SubStepV2 } from "../SubStep/Index";
import "./styles.css";

export type SubStepsScreenV2Props = {
  name: string;
  element: ReactNode;
  required?: boolean;
};

type StepByStepV2Props = {
  title: string;
  subtitle?: string;
  stepNumber: number;
  subSteps?: Array<SubStepsScreenV2Props>;
  lastStepAsSummary?: boolean;
};

function getStepState(
  screens: ScreensProps[] | undefined,
  isCurrent: boolean,
  isPast: boolean
): UserProgressScreensProps | "current" {
  if (screens?.some((screen) => screen.type === "error")) return "error";
  if (isCurrent) return "current";
  if (!isPast || !screens) return "nothing";
  return screens.every((screen) => screen.type !== "skiped" && screen.type !== "nothing") ? "advanced" : "skiped";
}

export function StepByStepV2({
  stepNumber,
  subSteps,
  title,
  subtitle,
  lastStepAsSummary = false,
}: StepByStepV2Props) {
  const {
    setAndRederTopScreen,
    updateUserProgress,
    setScreenNumber,
    subScreenNumber,
    setShowScreen,
    userProgress,
    screenNumber,
  } = useOnboarding();

  const [state, setState] = useState<UserProgressScreensProps | "current">("nothing");
  const [canNavigate, setCanNavigate] = useState(false);
  const showSubSteps = (subSteps?.length || 0) > 1;

  useEffect(() => {
    const progress = userProgress.find((p) => p.section === stepNumber);
    setState(getStepState(progress?.screens, screenNumber === stepNumber, stepNumber < screenNumber));
    setCanNavigate(
      screenNumber === stepNumber ||
      !!progress?.screens?.some((screen) => screen.type !== "nothing")
    );
  }, [screenNumber, stepNumber, userProgress]);

  useEffect(() => {
    const screens: Array<ScreensProps> = [];
    subSteps?.forEach((subStep, index) => {
      screens.push({
        name: subStep.name,
        element: subStep.element,
        type: index === 0 && stepNumber === 1 ? "visualized" : "nothing",
        required: subStep.required,
      });
    });

    updateUserProgress({
      section: stepNumber,
      screens,
    });

    if (stepNumber === 1) {
      setShowScreen([{ section: stepNumber, screens }], 1, 0);
    }
    // Re-registra quando o número da seção muda (ex.: Resumo ao adicionar fazenda). Não incluir `subSteps` nas deps para não resetar o progresso a cada render do pai.
  }, [stepNumber, updateUserProgress, setShowScreen]);

  return (
    <section className="step-v2">
      {stepNumber !== 1 && <div className="step-v2__line" />}
      <div className={`step-v2__marker step-v2__marker--${state}`}>
        <button
          className="step-v2__title"
          type="button"
          onClick={() => {
            if (!canNavigate) return;
            setScreenNumber(stepNumber);
            setAndRederTopScreen(userProgress, stepNumber, subScreenNumber);
          }}
          disabled={!canNavigate}
        >
          <strong>{title}</strong>
          {subtitle && <span>{subtitle}</span>}
        </button>
      </div>

      {showSubSteps && (
        <AnimateHeight height={screenNumber !== stepNumber ? 0 : "auto"}>
          <div className="step-v2__substeps">
            {subSteps?.map((subStep, index) => (
              <SubStepV2
                key={index}
                subStep={subStep}
                sectionNumb={stepNumber}
                screenNumb={index}
                isSummary={lastStepAsSummary && index === subSteps.length - 1}
              />
            ))}
          </div>
        </AnimateHeight>
      )}
    </section>
  );
}
